import { ComponentType, FC, ReactNode, SVGProps } from "react";
import {
  AdjustmentsHorizontalIcon,
  ArrowLeftIcon,
  ArrowUpRightIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  IdentificationIcon,
  KeyIcon,
  MapPinIcon,
  MinusIcon,
  PhoneIcon,
  PhotoIcon,
  PlusIcon,
  ShieldCheckIcon,
  SwatchIcon,
  TagIcon,
  UsersIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

/**
 * Design B's one thin line-icon set (1NOW-11175): Heroicons outline (already in the repo, 1.5px
 * stroke, `currentColor`). Everything Design B draws as an icon comes from here, so the home
 * page, the cards, the filters and the footer share one weight and one style.
 *
 * Heroicons has no car, no door and no brand marks, so those few are drawn on the same 24px
 * grid at the same 1.5px stroke (CUSTOM below).
 */
const HERO: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  user: UsersIcon,
  gear: Cog6ToothIcon,
  palette: SwatchIcon,
  shield: ShieldCheckIcon,
  tag: TagIcon,
  pin: MapPinIcon,
  headset: ChatBubbleLeftRightIcon,
  clock: ClockIcon,
  check: CheckCircleIcon,
  key: KeyIcon,
  phone: PhoneIcon,
  mail: EnvelopeIcon,
  arrowUpRight: ArrowUpRightIcon,
  chevronDown: ChevronDownIcon,
  close: XMarkIcon,
  plus: PlusIcon,
  minus: MinusIcon,
  sliders: AdjustmentsHorizontalIcon,
  calendar: CalendarDaysIcon,
  image: PhotoIcon,
  globe: GlobeAltIcon,
  tick: CheckIcon,
  chevronLeft: ChevronLeftIcon,
  chevronRight: ChevronRightIcon,
  arrowLeft: ArrowLeftIcon,
  id: IdentificationIcon,
};

const CUSTOM: Record<string, ReactNode> = {
  car: (
    <>
      <path d="M5 16H3.5v-4.2l2-4.6A2 2 0 0 1 7.3 6h9.4a2 2 0 0 1 1.8 1.2l2 4.6V16H19" />
      <path d="M3.5 11.8h17" />
      <circle cx="7.5" cy="16.5" r="2" />
      <circle cx="16.5" cy="16.5" r="2" />
      <path d="M9.5 16.5h5" />
    </>
  ),
  door: (
    <>
      <path d="M6.5 21V4.5A1.5 1.5 0 0 1 8 3h8a1.5 1.5 0 0 1 1.5 1.5V21" />
      <path d="M4.5 21h15M14.5 12h.01" />
    </>
  ),
  // Heroicons has no fuel pump or gauge either: drawn on the same grid (1NOW-11219).
  fuel: <path d="M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16M3.5 21h13M7.5 7.5h5M15 10h1.5a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 0 3 0V8.5L18.5 6" />,
  gauge: <path d="M3.75 17.25a8.25 8.25 0 1 1 16.5 0M12 17.25l3.5-4.5M12 17.25h.01M7 13.5l1 .6M12 9v1.2" />,
  instagram: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <path d="M17.2 6.8h.01" />
    </>
  ),
  facebook: <path d="M14 8h2.5V4.5H14A4 4 0 0 0 10 8.5V11H7.5v3.5H10V21h3.5v-6.5H16l.5-3.5h-3V8.7c0-.4.3-.7.7-.7Z" />,
  x: <path d="M4.5 4.5l15 15M19.5 4.5l-15 15" />,
  youtube: (
    <>
      <rect x="2.5" y="5.5" width="19" height="13" rx="4" />
      <path d="m10.5 9.5 4 2.5-4 2.5v-5Z" />
    </>
  ),
};

export type IconName = keyof typeof HERO | keyof typeof CUSTOM;

export const DbIcon: FC<{ name: IconName } & Omit<SVGProps<SVGSVGElement>, "name">> = ({
  name,
  className = "h-5 w-5",
  ...rest
}) => {
  const Hero = HERO[name];
  if (Hero) {
    return <Hero aria-hidden="true" focusable="false" data-icon={name} className={`shrink-0 ${className}`} {...rest} />;
  }
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      data-icon={name}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${className}`}
      {...rest}
    >
      {CUSTOM[name]}
    </svg>
  );
};

export default DbIcon;
